import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowRightBold,
    PiBriefcaseBold,
    PiCameraBold,
    PiCheckBold,
    PiCheckCircleFill,
    PiClockCounterClockwise,
    PiEnvelopeSimpleBold,
    PiGearSixBold,
    PiKeyBold,
    PiLinkBold,
    PiLockKeyBold,
    PiMagnifyingGlassBold,
    PiProhibitBold,
    PiShieldCheckBold,
    PiShieldWarningBold,
    PiUserBold,
    PiWarningBold,
} from 'react-icons/pi';
import { Button, PageHeader, Skeleton } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { DeactivatedBanner, DeactivationButton } from '@v2/features/users/UserDeactivation';
import { PrefixInput, settingsInputClass } from '@v2/features/users/UserSettingsFields';

type RoleOption = {
    name: string;
    label: string;
    users_count?: number;
    permissions_count?: number;
    is_system?: boolean;
};
type SelectOption = { value: string; label: string };

type User = {
    id: number;
    name: string;
    email: string;
    phone: string | null;
    department: string | null;
    job_title: string | null;
    locale: string | null;
    timezone: string | null;
    bio: string | null;
    avatar_url: string | null;
    role_names: string[];
    roles: Array<{ name: string; label?: string; color: string }>;
    is_active?: boolean;
    deactivated_at?: string | null;
    deactivation_reason?: string | null;
    deactivated_by?: string | null;
    ploomes_id?: number | null;
    ploomes_suspended?: boolean;
    ploomes_suspended_at?: string | null;
    last_login_at?: string | null;
    last_login_label?: string | null;
    created_at?: string | null;
};

type FormOptions = {
    roles: RoleOption[];
    locales: SelectOption[];
    timezones: SelectOption[];
    departments?: string[];
    job_titles?: string[];
};

type ActivityPreview = {
    data: Array<{
        id: string;
        title: string;
        human: string;
        category: string;
        count: number;
        alert: boolean;
    }>;
};

type FormState = {
    name: string;
    email: string;
    password: string;
    phone: string;
    department: string;
    job_title: string;
    locale: string;
    timezone: string;
    bio: string;
    roles: string[];
};

const FALLBACK = '/img/favicon.png';

type Tab = 'profile' | 'roles' | 'access';

const TABS: { key: Tab; label: string; icon: ReactNode }[] = [
    { key: 'profile', label: 'Dados', icon: <PiUserBold className="size-4" /> },
    { key: 'roles', label: 'Perfis de acesso', icon: <PiShieldCheckBold className="size-4" /> },
    { key: 'access', label: 'Acesso e senha', icon: <PiLockKeyBold className="size-4" /> },
];

const TAB_FIELDS: Record<Tab, (keyof FormState | 'avatar')[]> = {
    profile: ['name', 'email', 'phone', 'department', 'job_title', 'bio', 'locale', 'timezone', 'avatar'],
    roles: ['roles'],
    access: ['password'],
};

const fromUser = (u: User): FormState => ({
    name: u.name ?? '',
    email: u.email ?? '',
    password: '',
    phone: u.phone ?? '',
    department: u.department ?? '',
    job_title: u.job_title ?? '',
    locale: u.locale ?? '',
    timezone: u.timezone ?? '',
    bio: u.bio ?? '',
    roles: [...(u.role_names ?? [])].sort(),
});

const sameForm = (a: FormState, b: FormState) =>
    JSON.stringify({ ...a, roles: [...a.roles].sort() }) === JSON.stringify({ ...b, roles: [...b.roles].sort() });

function relative(iso?: string | null): string | null {
    if (!iso) return null;
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days <= 0) return 'hoje';
    if (days === 1) return 'ontem';
    if (days < 30) return `há ${days} dias`;
    const months = Math.floor(days / 30);
    return months < 12 ? `há ${months} ${months === 1 ? 'mês' : 'meses'}` : `há ${Math.floor(months / 12)} ano(s)`;
}

function Section({
    icon,
    title,
    description,
    action,
    children,
}: {
    icon: ReactNode;
    title: string;
    description?: string;
    action?: ReactNode;
    children: ReactNode;
}) {
    return (
        <section className="rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/60">
            <header className="flex items-start gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {icon}
                </span>
                <div className="min-w-0 flex-1">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
                    {description && <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>}
                </div>
                {action}
            </header>
            <div className="p-5">{children}</div>
        </section>
    );
}

function Field({
    label,
    hint,
    error,
    required,
    children,
    className,
}: {
    label: string;
    hint?: string;
    error?: string;
    required?: boolean;
    children: ReactNode;
    className?: string;
}) {
    return (
        <label className={cn('block', className)}>
            <span className="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-200">
                {label}
                {required && <span className="ml-0.5 text-rose-500">*</span>}
            </span>
            {children}
            {error ? (
                <span className="mt-1 block text-xs text-rose-600">{error}</span>
            ) : (
                hint && <span className="mt-1 block text-[11px] text-gray-400">{hint}</span>
            )}
        </label>
    );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex items-start justify-between gap-3 py-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">{label}</span>
            <span className="text-right font-medium text-gray-900 dark:text-gray-100">{children}</span>
        </div>
    );
}

export default function UserDetailPage() {
    const { id } = useParams<{ id: string }>();
    const qc = useQueryClient();
    const fileRef = useRef<HTMLInputElement>(null);
    const [form, setForm] = useState<FormState | null>(null);
    const [avatar, setAvatar] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [roleSearch, setRoleSearch] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const tab: Tab = TABS.some((t) => t.key === searchParams.get('tab')) ? (searchParams.get('tab') as Tab) : 'profile';
    const changeTab = (next: Tab) =>
        setSearchParams(
            (prev) => {
                const p = new URLSearchParams(prev);
                if (next === 'profile') p.delete('tab');
                else p.set('tab', next);
                return p;
            },
            { replace: true },
        );

    const userQuery = useQuery({
        queryKey: ['users', id],
        queryFn: async (): Promise<User> => (await api.get(`/users/${id}`)).data.data,
    });

    const optionsQuery = useQuery({
        queryKey: ['users', 'form-options'],
        queryFn: async (): Promise<FormOptions> => (await api.get('/users-form-options')).data,
        staleTime: 5 * 60_000,
    });

    const activityQuery = useQuery({
        queryKey: ['user-activity-preview', id],
        queryFn: async (): Promise<ActivityPreview> =>
            (
                await api.get(`/users/${id}/activity-log`, {
                    params: { days: 90 },
                })
            ).data,
        staleTime: 60_000,
    });

    const initial = useMemo(() => (userQuery.data ? fromUser(userQuery.data) : null), [userQuery.data]);

    useEffect(() => {
        if (initial) {
            setForm(initial);
            setPreview(userQuery.data?.avatar_url ?? null);
            setAvatar(null);
        }
    }, [initial, userQuery.data?.avatar_url]);

    const dirty = !!form && !!initial && (!sameForm(form, initial) || avatar !== null);

    const tabDirty = useMemo(() => {
        const out = { profile: false, roles: false, access: false } as Record<Tab, boolean>;
        if (!form || !initial) return out;
        (Object.keys(TAB_FIELDS) as Tab[]).forEach((t) => {
            out[t] = TAB_FIELDS[t].some((f) =>
                f === 'avatar'
                    ? avatar !== null
                    : JSON.stringify(f === 'roles' ? [...form.roles].sort() : form[f]) !== JSON.stringify(initial[f]),
            );
        });
        return out;
    }, [form, initial, avatar]);

    const tabErrors = useMemo(() => {
        const keys = Object.keys(fieldErrors).map((k) => k.split('.')[0]);
        return Object.fromEntries(
            (Object.keys(TAB_FIELDS) as Tab[]).map((t) => [t, TAB_FIELDS[t].some((f) => keys.includes(f))]),
        ) as Record<Tab, boolean>;
    }, [fieldErrors]);

    useEffect(() => {
        if (!dirty) return;
        const warn = (e: BeforeUnloadEvent) => e.preventDefault();
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

    const toggleRole = (name: string) =>
        setForm((prev) =>
            prev
                ? {
                      ...prev,
                      roles: prev.roles.includes(name) ? prev.roles.filter((r) => r !== name) : [...prev.roles, name],
                  }
                : prev,
        );

    const discard = () => {
        if (!initial) return;
        setForm(initial);
        setAvatar(null);
        setPreview(userQuery.data?.avatar_url ?? null);
        setFieldErrors({});
        setShowPassword(false);
    };

    const mutation = useMutation({
        mutationFn: async () => {
            if (!form) return null;
            const fd = new FormData();
            fd.append('_method', 'PUT');
            const append = (k: string, v: unknown) => {
                if (v === null || v === undefined || v === '') return;
                fd.append(k, String(v));
            };
            append('name', form.name);
            append('email', form.email);
            fd.append('phone', form.phone);
            fd.append('department', form.department);
            fd.append('job_title', form.job_title);
            fd.append('locale', form.locale);
            fd.append('timezone', form.timezone);
            fd.append('bio', form.bio);
            if (form.password) append('password', form.password);
            form.roles.forEach((r, i) => fd.append(`roles[${i}]`, r));
            if (avatar) fd.append('avatar', avatar);
            return (
                await api.post(`/users/${id}`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
            ).data;
        },
        onSuccess: () => {
            setFieldErrors({});
            setShowPassword(false);
            toast.success('Usuário atualizado.');
            qc.invalidateQueries({ queryKey: ['users'] });
            qc.invalidateQueries({ queryKey: ['user-activity-preview', id] });
        },
        onError: (err: {
            response?: {
                status?: number;
                data?: { message?: string; errors?: Record<string, string[]> };
            };
        }) => {
            if (err?.response?.status === 422) {
                const errors = err.response.data?.errors ?? {};
                setFieldErrors(errors);
                const keys = Object.keys(errors).map((k) => k.split('.')[0]);
                const first = (Object.keys(TAB_FIELDS) as Tab[]).find((t) => TAB_FIELDS[t].some((f) => keys.includes(f)));
                if (first) changeTab(first);
                toast.error(err.response.data?.message ?? 'Verifique os campos destacados.');
            } else {
                toast.error(err?.response?.data?.message ?? 'Erro ao salvar.');
            }
        },
    });

    const welcomeMutation = useMutation({
        mutationFn: async () => (await api.post(`/users/${id}/send-welcome`)).data,
        onSuccess: (res: { message: string }) => toast.success(res.message),
        onError: () => toast.error('Falha ao enviar o convite.'),
    });

    const resetMutation = useMutation({
        mutationFn: async () => (await api.post(`/users/${id}/send-password-reset`)).data,
        onSuccess: (res: { message: string }) => toast.success(res.message),
        onError: (err: { response?: { data?: { message?: string } } }) =>
            toast.error(err?.response?.data?.message ?? 'Falha ao enviar o link de redefinição.'),
    });

    const opts = optionsQuery.data;
    const roles = useMemo(() => {
        const list = opts?.roles ?? [];
        const term = roleSearch.trim().toLowerCase();
        return term ? list.filter((r) => `${r.label} ${r.name}`.toLowerCase().includes(term)) : list;
    }, [opts?.roles, roleSearch]);
    const roleLabel = (name: string) => opts?.roles.find((r) => r.name === name)?.label ?? name;

    if (userQuery.isLoading || !form) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-16 w-1/2 rounded-xl" />
                <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
                    <div className="space-y-5">
                        <Skeleton className="h-72 rounded-2xl" />
                        <Skeleton className="h-48 rounded-2xl" />
                    </div>
                    <Skeleton className="h-80 rounded-2xl" />
                </div>
            </div>
        );
    }
    if (userQuery.isError || !userQuery.data) {
        return <p className="text-sm text-rose-600">Não foi possível carregar o usuário.</p>;
    }

    const user = userQuery.data;
    const fe = (k: string) => fieldErrors[k]?.[0];
    const active = user.is_active !== false;
    const recent = (activityQuery.data?.data ?? []).slice(0, 6);
    const addedRoles = form.roles.filter((r) => !initial?.roles.includes(r));
    const removedRoles = (initial?.roles ?? []).filter((r) => !form.roles.includes(r));

    return (
        <div className={cn('@container', dirty && 'pb-24')}>
            <PageHeader
                title={user.name}
                breadcrumb={[
                    { name: 'Dashboard', href: '/' },
                    { name: 'Administração' },
                    { name: 'Usuários', href: '/users' },
                    { name: user.name },
                ]}
            >
                <div className="flex flex-wrap items-center gap-2">
                    <Link
                        to={`/users/${id}/activity-log`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        <PiClockCounterClockwise className="size-4" />
                        Atividade
                    </Link>
                    <DeactivationButton user={user} />
                </div>
            </PageHeader>

            <DeactivatedBanner user={user} />

            <div className="grid items-start gap-5 @5xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="space-y-5">
                    <nav
                        className="flex gap-1 overflow-x-auto rounded-xl border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800/60"
                        role="tablist"
                    >
                        {TABS.map((t) => {
                            const changed = tabDirty[t.key];
                            const hasError = tabErrors[t.key];
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === t.key}
                                    onClick={() => changeTab(t.key)}
                                    className={cn(
                                        'inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition',
                                        tab === t.key
                                            ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                                            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
                                    )}
                                >
                                    {t.icon}
                                    {t.label}
                                    {t.key === 'roles' && (
                                        <span
                                            className={cn(
                                                'rounded-full px-1.5 text-[11px] tabular-nums',
                                                tab === t.key
                                                    ? 'bg-white/20'
                                                    : form.roles.length === 0
                                                      ? 'bg-amber-100 text-amber-700'
                                                      : 'bg-gray-100 dark:bg-gray-700',
                                            )}
                                        >
                                            {form.roles.length}
                                        </span>
                                    )}
                                    {hasError ? (
                                        <span className="size-2 rounded-full bg-rose-500" title="Campo com erro" />
                                    ) : (
                                        changed && <span className="size-2 rounded-full bg-amber-400" title="Alterações não salvas" />
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                    {tab === 'profile' && (
                        <div className="space-y-5">
                            <Section
                                icon={<PiUserBold />}
                                title="Dados pessoais"
                                description="Nome e contato. O e-mail é o login do Hub e do app."
                            >
                                <div className="mb-5 flex items-center gap-4">
                                    <button
                                        type="button"
                                        onClick={() => fileRef.current?.click()}
                                        className="group relative size-20 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200 dark:bg-gray-700 dark:ring-gray-600"
                                        aria-label="Trocar foto"
                                    >
                                        <img
                                            src={preview || FALLBACK}
                                            alt=""
                                            className="size-full object-cover"
                                            onError={(e) => ((e.target as HTMLImageElement).src = FALLBACK)}
                                        />
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-white opacity-0 transition group-hover:opacity-100">
                                            <PiCameraBold className="size-6" />
                                        </span>
                                    </button>
                                    <div className="text-sm">
                                        <button
                                            type="button"
                                            onClick={() => fileRef.current?.click()}
                                            className="font-semibold text-primary hover:underline"
                                        >
                                            Trocar foto
                                        </button>
                                        {avatar && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setAvatar(null);
                                                    setPreview(user.avatar_url);
                                                }}
                                                className="ml-3 text-gray-500 hover:underline"
                                            >
                                                Desfazer
                                            </button>
                                        )}
                                        <p className="text-xs text-gray-400">PNG, JPG ou WebP até 5 MB.</p>
                                        {fe('avatar') && <p className="text-xs text-rose-600">{fe('avatar')}</p>}
                                    </div>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0] ?? null;
                                            e.target.value = '';
                                            if (!file) return;
                                            if (file.size > 5 * 1024 * 1024) {
                                                toast.error('A foto passa de 5 MB.');
                                                return;
                                            }
                                            setAvatar(file);
                                            setPreview(URL.createObjectURL(file));
                                        }}
                                    />
                                </div>
                                <div className="grid gap-4 @2xl:grid-cols-2">
                                    <Field label="Nome completo" required error={fe('name')} className="@2xl:col-span-2">
                                        <input
                                            className={settingsInputClass}
                                            value={form.name}
                                            onChange={(e) => set('name', e.target.value)}
                                        />
                                    </Field>
                                    <Field label="E-mail" required error={fe('email')} hint="Usado para entrar no Hub e no app.">
                                        <input
                                            type="email"
                                            className={settingsInputClass}
                                            value={form.email}
                                            onChange={(e) => set('email', e.target.value)}
                                            placeholder="nome@bucklerfit.com.br"
                                        />
                                    </Field>
                                    <Field label="Telefone" error={fe('phone')}>
                                        <PrefixInput
                                            prefix="+55"
                                            value={form.phone}
                                            onChange={(v) => set('phone', v)}
                                            placeholder="11999998888"
                                        />
                                    </Field>
                                </div>
                            </Section>
                            <Section
                                icon={<PiBriefcaseBold />}
                                title="Trabalho"
                                description="Aparece na lista de usuários e ajuda a achar a pessoa."
                            >
                                <div className="grid gap-4 @2xl:grid-cols-2">
                                    <Field label="Cargo" error={fe('job_title')}>
                                        <input
                                            list="user-job-titles"
                                            className={settingsInputClass}
                                            value={form.job_title}
                                            onChange={(e) => set('job_title', e.target.value)}
                                            placeholder="Ex.: Analista de Comex"
                                        />
                                        <datalist id="user-job-titles">
                                            {opts?.job_titles?.map((v) => (
                                                <option key={v} value={v} />
                                            ))}
                                        </datalist>
                                    </Field>
                                    <Field label="Departamento" error={fe('department')}>
                                        <input
                                            list="user-departments"
                                            className={settingsInputClass}
                                            value={form.department}
                                            onChange={(e) => set('department', e.target.value)}
                                            placeholder="Ex.: Comércio Exterior"
                                        />
                                        <datalist id="user-departments">
                                            {opts?.departments?.map((v) => (
                                                <option key={v} value={v} />
                                            ))}
                                        </datalist>
                                    </Field>
                                    <Field label="Observações" error={fe('bio')} className="@2xl:col-span-2">
                                        <textarea
                                            className={cn(settingsInputClass, 'min-h-20')}
                                            value={form.bio}
                                            onChange={(e) => set('bio', e.target.value)}
                                            placeholder="Ex.: cobre férias da logística; responsável pelos embarques de RO"
                                        />
                                    </Field>
                                </div>
                            </Section>
                            <Section icon={<PiGearSixBold />} title="Preferências" description="Idioma e fuso das telas e dos e-mails.">
                                <div className="grid gap-4 @2xl:grid-cols-2">
                                    <Field label="Idioma" error={fe('locale')}>
                                        <select
                                            className={settingsInputClass}
                                            value={form.locale}
                                            onChange={(e) => set('locale', e.target.value)}
                                        >
                                            <option value="">Padrão do sistema (Português)</option>
                                            {opts?.locales.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Fuso horário" error={fe('timezone')}>
                                        <select
                                            className={settingsInputClass}
                                            value={form.timezone}
                                            onChange={(e) => set('timezone', e.target.value)}
                                        >
                                            <option value="">Padrão do sistema (São Paulo)</option>
                                            {opts?.timezones.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>
                            </Section>
                        </div>
                    )}
                    {tab === 'roles' && (
                        <Section
                            icon={<PiShieldCheckBold />}
                            title="Perfis de acesso"
                            description="O que a pessoa pode ver e fazer. Pode ter mais de um perfil."
                            action={
                                <Link to="/roles-permissions" className="shrink-0 text-xs font-semibold text-primary hover:underline">
                                    Funções e permissões
                                </Link>
                            }
                        >
                            {form.roles.length === 0 && (
                                <div className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                                    <PiShieldWarningBold className="mt-0.5 size-4 shrink-0" />
                                    Sem perfil, a pessoa entra mas só vê a tela Início.
                                </div>
                            )}
                            {(opts?.roles.length ?? 0) > 8 && (
                                <label className="relative mb-3 block">
                                    <PiMagnifyingGlassBold className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        value={roleSearch}
                                        onChange={(e) => setRoleSearch(e.target.value)}
                                        placeholder="Buscar perfil"
                                        className={cn(settingsInputClass, 'pl-8')}
                                    />
                                </label>
                            )}
                            {!opts ? (
                                <Skeleton className="h-24 rounded-lg" />
                            ) : (
                                <div className="space-y-4">
                                    {[
                                        {
                                            key: 'current',
                                            title: null as string | null,
                                            list: roles.filter((r) => !r.label.includes('(legado)')),
                                        },
                                        {
                                            key: 'legacy',
                                            title: 'Perfis antigos (legado) — evite usar em gente nova',
                                            list: roles.filter((r) => r.label.includes('(legado)')),
                                        },
                                    ]
                                        .filter((g) => g.list.length > 0)
                                        .map(({ key, title, list }) => (
                                            <div key={key}>
                                                {title && (
                                                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                        {title}
                                                    </p>
                                                )}
                                                <div className="grid gap-2 @2xl:grid-cols-2 @5xl:grid-cols-3">
                                                    {list.map((role) => {
                                                        const on = form.roles.includes(role.name);
                                                        return (
                                                            <button
                                                                key={role.name}
                                                                type="button"
                                                                onClick={() => toggleRole(role.name)}
                                                                aria-pressed={on}
                                                                className={cn(
                                                                    'flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition',
                                                                    on
                                                                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/40',
                                                                )}
                                                            >
                                                                <span
                                                                    className={cn(
                                                                        'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px]',
                                                                        on
                                                                            ? 'border-primary bg-primary text-white'
                                                                            : 'border-gray-300 text-transparent dark:border-gray-500',
                                                                    )}
                                                                >
                                                                    <PiCheckBold />
                                                                </span>
                                                                <span className="min-w-0">
                                                                    <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                                                                        {role.label}
                                                                    </span>
                                                                    {role.users_count !== undefined && (
                                                                        <span className="block text-[11px] text-gray-400">
                                                                            {role.users_count} pessoa
                                                                            {role.users_count === 1 ? '' : 's'} ·{' '}
                                                                            {role.permissions_count ?? 0} permiss
                                                                            {role.permissions_count === 1 ? 'ão' : 'ões'}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    {roles.length === 0 && <p className="text-sm text-gray-500">Nenhum perfil encontrado.</p>}
                                </div>
                            )}
                        </Section>
                    )}
                    {tab === 'access' && (
                        <Section
                            icon={<PiLockKeyBold />}
                            title="Acesso e senha"
                            description="Prefira mandar o link: a pessoa cria a própria senha e você não precisa saber qual é."
                        >
                            <div className="grid gap-3 @2xl:grid-cols-2">
                                <button
                                    type="button"
                                    disabled={!active || resetMutation.isPending}
                                    onClick={() => resetMutation.mutate()}
                                    className="flex items-start gap-3 rounded-xl border border-gray-200 p-3 text-left transition hover:border-primary hover:bg-primary/5 disabled:opacity-50 dark:border-gray-600"
                                >
                                    <PiKeyBold className="mt-0.5 size-5 shrink-0 text-primary" />
                                    <span>
                                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            {resetMutation.isPending ? 'Enviando…' : 'Enviar link para trocar a senha'}
                                        </span>
                                        <span className="block text-xs text-gray-500">Para quem esqueceu a senha.</span>
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    disabled={!active || welcomeMutation.isPending}
                                    onClick={() => welcomeMutation.mutate()}
                                    className="flex items-start gap-3 rounded-xl border border-gray-200 p-3 text-left transition hover:border-primary hover:bg-primary/5 disabled:opacity-50 dark:border-gray-600"
                                >
                                    <PiEnvelopeSimpleBold className="mt-0.5 size-5 shrink-0 text-primary" />
                                    <span>
                                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            {welcomeMutation.isPending ? 'Enviando…' : 'Reenviar convite de boas-vindas'}
                                        </span>
                                        <span className="block text-xs text-gray-500">Para quem nunca entrou no Hub.</span>
                                    </span>
                                </button>
                            </div>
                            {!active && (
                                <p className="mt-2 text-xs text-gray-500">Usuário desativado: reative antes de mandar link ou convite.</p>
                            )}
                            <div className="mt-4 border-t border-gray-100 pt-3 dark:border-gray-700">
                                {showPassword ? (
                                    <Field
                                        label="Nova senha"
                                        hint="Mínimo 8 caracteres. Vale a partir do momento em que você salvar."
                                        error={fe('password')}
                                    >
                                        <input
                                            type="password"
                                            autoComplete="new-password"
                                            className={cn(settingsInputClass, '@2xl:max-w-sm')}
                                            value={form.password}
                                            onChange={(e) => set('password', e.target.value)}
                                            placeholder="••••••••"
                                            autoFocus
                                        />
                                    </Field>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(true)}
                                        className="text-xs font-semibold text-gray-500 hover:text-gray-800 hover:underline dark:hover:text-gray-200"
                                    >
                                        Definir uma senha manualmente
                                    </button>
                                )}
                            </div>
                        </Section>
                    )}
                </div>

                <aside className="space-y-5 @5xl:sticky @5xl:top-20">
                    <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800/60">
                        <div className="mb-3 flex items-center gap-3">
                            <img
                                src={preview || FALLBACK}
                                alt=""
                                className="size-12 rounded-full object-cover"
                                onError={(e) => ((e.target as HTMLImageElement).src = FALLBACK)}
                            />
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{form.name || user.name}</p>
                                <p className="truncate text-xs text-gray-500">
                                    {[form.job_title, form.department].filter(Boolean).join(' · ') || user.email}
                                </p>
                            </div>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            <InfoRow label="Situação">
                                {active ? (
                                    <span className="inline-flex items-center gap-1 text-emerald-600">
                                        <PiCheckCircleFill /> Ativo
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-rose-600">
                                        <PiProhibitBold /> Desativado
                                    </span>
                                )}
                            </InfoRow>
                            <InfoRow label="Último acesso">
                                {user.last_login_label ? (
                                    <span title={user.last_login_label}>{relative(user.last_login_at)}</span>
                                ) : (
                                    <span className="font-normal text-gray-400">sem registro</span>
                                )}
                            </InfoRow>
                            <InfoRow label="No Hub desde">
                                {user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '—'}
                            </InfoRow>
                            <InfoRow label="Ploomes">
                                {user.ploomes_id ? (
                                    <span className="inline-flex flex-col items-end">
                                        <span className="inline-flex items-center gap-1">
                                            <PiLinkBold className="text-gray-400" /> #{user.ploomes_id}
                                        </span>
                                        {user.ploomes_suspended && (
                                            <span className="mt-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                                                Suspenso no Ploomes
                                                {user.ploomes_suspended_at ? ` em ${user.ploomes_suspended_at}` : ''}
                                            </span>
                                        )}
                                    </span>
                                ) : (
                                    <span className="font-normal text-gray-400">sem vínculo</span>
                                )}
                            </InfoRow>
                        </div>
                        <div className="mt-3">
                            <p className="mb-1.5 text-xs font-semibold text-gray-500">Perfis</p>
                            {form.roles.length === 0 ? (
                                <p className="text-xs text-amber-700 dark:text-amber-300">Nenhum perfil</p>
                            ) : (
                                <div className="flex flex-wrap gap-1.5">
                                    {form.roles.map((r) => (
                                        <span
                                            key={r}
                                            className={cn(
                                                'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                                                addedRoles.includes(r)
                                                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900'
                                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
                                            )}
                                        >
                                            {addedRoles.includes(r) && '+ '}
                                            {roleLabel(r)}
                                        </span>
                                    ))}
                                    {removedRoles.map((r) => (
                                        <span
                                            key={r}
                                            className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 line-through ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900"
                                        >
                                            {roleLabel(r)}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/60">
                        <header className="flex items-center justify-between border-b border-gray-100 px-5 py-3 dark:border-gray-700">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Atividade recente</h2>
                            <Link
                                to={`/users/${id}/activity-log`}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            >
                                Ver tudo <PiArrowRightBold className="size-3" />
                            </Link>
                        </header>
                        <div className="px-5 py-3">
                            {activityQuery.isLoading ? (
                                <div className="space-y-2">
                                    {[0, 1, 2].map((i) => (
                                        <Skeleton key={i} className="h-8 rounded" />
                                    ))}
                                </div>
                            ) : recent.length === 0 ? (
                                <p className="py-4 text-center text-xs text-gray-400">Nada nos últimos 90 dias.</p>
                            ) : (
                                <ol className="space-y-2.5">
                                    {recent.map((a) => (
                                        <li key={a.id} className="flex items-start gap-2 text-xs">
                                            <span
                                                className={cn(
                                                    'mt-1 size-1.5 shrink-0 rounded-full',
                                                    a.alert ? 'bg-rose-500' : 'bg-gray-300 dark:bg-gray-500',
                                                )}
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span
                                                    className={cn(
                                                        'block text-gray-800 dark:text-gray-200',
                                                        a.alert && 'text-rose-700 dark:text-rose-300',
                                                    )}
                                                >
                                                    {a.title}
                                                    {a.count > 1 && <span className="text-gray-400"> ×{a.count}</span>}
                                                </span>
                                                <span className="text-gray-400">{a.human}</span>
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    </section>
                </aside>
            </div>

            {dirty && (
                <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95 lg:left-[270px]">
                    <div className="flex flex-wrap items-center gap-3 px-6 py-3">
                        <PiWarningBold className="size-5 text-amber-500" />
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                            Alterações não salvas
                            {(addedRoles.length > 0 || removedRoles.length > 0) && (
                                <span className="text-gray-500">
                                    {' '}
                                    · perfis:{' '}
                                    {[...addedRoles.map((r) => `+${roleLabel(r)}`), ...removedRoles.map((r) => `−${roleLabel(r)}`)].join(
                                        ', ',
                                    )}
                                </span>
                            )}
                        </p>
                        <div className="ml-auto flex gap-2">
                            <button
                                type="button"
                                onClick={discard}
                                disabled={mutation.isPending}
                                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                            >
                                Descartar
                            </button>
                            <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                                {mutation.isPending ? 'Salvando…' : 'Salvar alterações'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
